/**
 * Weather.
 *
 * Kate: "It can rain. It can thunderstorm. It can be a tornado. It can be all the
 * weather that is normal." And on how often: "Sunny is, uh, more common. It's the
 * commonest. And rain is also common. Tornadoes are really rare. Earthquakes are too."
 *
 * So five kinds in three rarity tiers — sunny commonest, rain common, thunderstorm in
 * between, tornado and earthquake rare. The weights below encode exactly that ordering
 * and nothing more; the specific numbers are an assumption, the ordering is hers.
 */
import * as THREE from 'three'

export type WeatherKind = 'sunny' | 'rain' | 'thunderstorm' | 'tornado' | 'earthquake'

interface WeatherSpec {
  kind: WeatherKind
  /** Relative likelihood of being picked next. Higher is more common. */
  weight: number
  /** Seconds this weather lasts, picked uniformly in range. */
  duration: [number, number]
}

/**
 * Tiers, commonest first. Sunny outweighs everything; rain is the other "common" one;
 * tornado and earthquake are the two she called "really rare" and are the rarest by a
 * wide margin. Earthquakes are brief — a quake is a jolt, not a season.
 */
const SPECS: WeatherSpec[] = [
  { kind: 'sunny', weight: 100, duration: [90, 240] },
  { kind: 'rain', weight: 45, duration: [60, 150] },
  { kind: 'thunderstorm', weight: 12, duration: [45, 100] },
  { kind: 'tornado', weight: 2, duration: [30, 60] },
  { kind: 'earthquake', weight: 2, duration: [8, 16] },
]

const TOTAL_WEIGHT = SPECS.reduce((sum, s) => sum + s.weight, 0)

/** How much each weather dims the daylight reaching the ground. 1 = no dimming. */
const LIGHT_SCALE: Record<WeatherKind, number> = {
  sunny: 1,
  rain: 0.62,
  thunderstorm: 0.42,
  tornado: 0.5,
  earthquake: 1,
}

/** How far the fog closes in. 1 = clear. */
const FOG_SCALE: Record<WeatherKind, number> = {
  sunny: 1,
  rain: 0.62,
  thunderstorm: 0.45,
  tornado: 0.5,
  earthquake: 1,
}

// 3000 drops over a 70m square is 0.6 per square metre --- drizzle, whatever the sky
// is doing. A thunderstorm has to look like one.
const RAIN_DROPS = 9000
const RAIN_AREA = 70
/**
 * How long a drop's streak is, as seconds of its own fall. Rain reads as rain because
 * each drop smears into a line while the eye holds it --- as round points it looked
 * like drifting dust however many there were.
 */
const RAIN_STREAK = { rain: 0.028, thunderstorm: 0.045 }
const RAIN_TOP = 26

/** Tornado funnel dimensions — tall enough to read against the treeline. */
const FUNNEL_HEIGHT = 34
const FUNNEL_TOP_RADIUS = 9
const FUNNEL_BOTTOM_RADIUS = 1.6
/** Metres per second the funnel wanders across the map. */
const FUNNEL_DRIFT = 4

export class Weather {
  current: WeatherKind = 'sunny'
  /** Seconds left before the next roll. */
  remaining = 0
  /** 0..1 ease into the current weather, so rain doesn't snap on. */
  private blend = 1
  private readonly random: () => number

  /** Rain particles, shown for rain and thunderstorm. */
  readonly rain: THREE.LineSegments
  private readonly rainVelocities: Float32Array
  /** Screen-shake offset during an earthquake; the camera adds this. */
  readonly shake = new THREE.Vector3()
  /** Lightning flash strength, 0..1, during a thunderstorm. */
  flash = 0
  private flashCooldown = 4

  /** The tornado itself. Visible only while `current` is 'tornado'. */
  readonly funnel: THREE.Mesh
  private funnelHeading = 0

  constructor(seed = 20260816) {
    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    // Two vertices per drop: the head, and a tail trailing above it.
    const positions = new Float32Array(RAIN_DROPS * 6)
    this.rainVelocities = new Float32Array(RAIN_DROPS)
    for (let i = 0; i < RAIN_DROPS; i++) {
      const x = (this.random() * 2 - 1) * RAIN_AREA
      const y = this.random() * RAIN_TOP
      const z = (this.random() * 2 - 1) * RAIN_AREA
      const speed = 18 + this.random() * 14
      this.rainVelocities[i] = speed
      positions[i * 6] = x
      positions[i * 6 + 1] = y
      positions[i * 6 + 2] = z
      positions[i * 6 + 3] = x
      positions[i * 6 + 4] = y + speed * RAIN_STREAK.rain
      positions[i * 6 + 5] = z
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    this.rain = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        color: 0xaac4d8,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    )
    this.rain.frustumCulled = false
    this.rain.visible = false

    // Kate listed the tornado as a weather type, so it has to be something you can see,
    // not just a dimming of the light. An open-ended cone, wide at the cloud and narrow
    // at the ground, spinning and drifting across the map.
    this.funnel = new THREE.Mesh(
      new THREE.CylinderGeometry(FUNNEL_TOP_RADIUS, FUNNEL_BOTTOM_RADIUS, FUNNEL_HEIGHT, 24, 6, true),
      new THREE.MeshStandardMaterial({
        color: 0x5a5346,
        roughness: 1,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    )
    this.funnel.position.y = FUNNEL_HEIGHT / 2
    this.funnel.visible = false

    this.roll()
  }

  /** Pick the next weather by weight. */
  private roll(): void {
    let ticket = this.random() * TOTAL_WEIGHT
    let spec = SPECS[SPECS.length - 1]!
    for (const candidate of SPECS) {
      ticket -= candidate.weight
      if (ticket <= 0) {
        spec = candidate
        break
      }
    }
    this.current = spec.kind
    const [min, max] = spec.duration
    this.remaining = min + this.random() * (max - min)
    this.blend = 0

    if (this.current === 'tornado') {
      // Touch down somewhere out on the map and pick a direction to wander.
      this.funnel.position.x = (this.random() * 2 - 1) * 30
      this.funnel.position.z = (this.random() * 2 - 1) * 30
      this.funnelHeading = this.random() * Math.PI * 2
    }
  }

  /** True while the sky is throwing water around. */
  get isWet(): boolean {
    return this.current === 'rain' || this.current === 'thunderstorm'
  }

  /** Advance one fixed simulation step. */
  update(dt: number, playerPosition: THREE.Vector3): void {
    this.remaining -= dt
    if (this.remaining <= 0) this.roll()
    this.blend = Math.min(1, this.blend + dt / 3)

    // Rain follows the player so a fixed particle box always surrounds them.
    this.rain.visible = this.isWet || this.blend < 1
    const positions = this.rain.geometry.getAttribute('position') as THREE.BufferAttribute
    const array = positions.array as Float32Array
    if (this.rain.visible) {
      const storm = this.current === 'thunderstorm'
      const speedScale = storm ? 1.35 : 1
      // Longer streaks in a storm: heavier rain, not merely more of it.
      const streak = storm ? RAIN_STREAK.thunderstorm : RAIN_STREAK.rain
      for (let i = 0; i < RAIN_DROPS; i++) {
        const head = i * 6
        const fall = this.rainVelocities[i]! * speedScale
        array[head + 1]! -= fall * dt
        if (array[head + 1]! < 0) {
          array[head]! = playerPosition.x + (this.random() * 2 - 1) * RAIN_AREA
          array[head + 1]! = RAIN_TOP
          array[head + 2]! = playerPosition.z + (this.random() * 2 - 1) * RAIN_AREA
        }
        // The tail follows the head, trailing by however far the drop falls in a
        // fraction of a second.
        array[head + 3]! = array[head]!
        array[head + 4]! = array[head + 1]! + fall * streak
        array[head + 5]! = array[head + 2]!
      }
      positions.needsUpdate = true
      const material = this.rain.material as THREE.LineBasicMaterial
      const target = this.isWet ? (storm ? 0.85 : 0.6) : 0
      material.opacity += (target - material.opacity) * Math.min(1, dt * 2)
    }

    // Lightning: short bright flashes, only in a thunderstorm.
    if (this.current === 'thunderstorm') {
      this.flashCooldown -= dt
      if (this.flashCooldown <= 0) {
        this.flash = 1
        this.flashCooldown = 2 + this.random() * 7
      }
    }
    this.flash = Math.max(0, this.flash - dt * 4)

    // Tornado: spin the funnel and drift it across the map.
    const funnelMaterial = this.funnel.material as THREE.MeshStandardMaterial
    if (this.current === 'tornado') {
      this.funnel.visible = true
      this.funnel.rotation.y += dt * 6
      this.funnel.position.x += Math.cos(this.funnelHeading) * FUNNEL_DRIFT * dt
      this.funnel.position.z += Math.sin(this.funnelHeading) * FUNNEL_DRIFT * dt
      // Turn back rather than wander off the map.
      if (Math.abs(this.funnel.position.x) > 40 || Math.abs(this.funnel.position.z) > 40) {
        this.funnelHeading += Math.PI
      }
      funnelMaterial.opacity = Math.min(0.72, funnelMaterial.opacity + dt * 0.5)
    } else if (funnelMaterial.opacity > 0) {
      funnelMaterial.opacity = Math.max(0, funnelMaterial.opacity - dt * 0.5)
      if (funnelMaterial.opacity <= 0) this.funnel.visible = false
    }

    // Earthquake: the ground shakes. Kate listed it beside tornadoes as "really rare".
    if (this.current === 'earthquake') {
      const strength = 0.22 * this.blend
      this.shake.set(
        (this.random() * 2 - 1) * strength,
        (this.random() * 2 - 1) * strength * 0.6,
        (this.random() * 2 - 1) * strength,
      )
    } else {
      this.shake.multiplyScalar(0.8)
    }
  }

  /** Multiplier applied to the sun after the day/night cycle has set it. */
  get lightScale(): number {
    return THREE.MathUtils.lerp(1, LIGHT_SCALE[this.current], this.blend)
  }

  /** Multiplier applied to fog distance. Smaller means the fog closes in. */
  get fogScale(): number {
    return THREE.MathUtils.lerp(1, FOG_SCALE[this.current], this.blend)
  }
}
